/**
 * Caja: de donde sale cada numero del dinero real.
 *
 * LA REGLA QUE ORGANIZA TODO ESTE ARCHIVO
 *
 * Solo se GUARDA lo que no existe en ninguna otra tabla. Todo lo demas se DERIVA:
 *
 *   cobrado   <- pedidos pagados, con su fecha de cobro
 *   compras   <- tickets de compra, que ya traen fecha y total
 *   guardado  -> aportaciones, retiros, devoluciones y gastos que no son insumos
 *
 * Duplicar los cobros o las compras en la tabla de caja habria sido mas facil de
 * consultar, y tambien la forma segura de acabar con dos cifras distintas para el
 * mismo dinero. Es el error que este proyecto lleva dias corrigiendo en otras
 * pantallas, asi que aqui se evita desde el principio.
 *
 * LO QUE ESTE ARCHIVO NO TOCA
 *
 * No escribe en ventas, pedidos, compras, produccion ni inventario. La formula de
 * la ganancia y las cuentas por cobrar quedan exactamente como estaban: esta vista
 * se suma al lado, no reemplaza nada.
 *
 * LA UNICA EXCEPCION, Y ESTA DOCUMENTADA
 *
 * `alternarPago` en repos/orders.ts ahora guarda tambien la fecha del cobro. Sin
 * ella no se puede saber en que semana entro el dinero, y con clientes que pagan
 * cada quince dias esa fecha es justo el dato que importa.
 */

import { db } from '../schema';
import { nuevoId } from '../ids';
import { enEscritura } from '../tx';
import { exigirTexto, textoOpcional } from '../validation';
import { ErrorValidacion } from '@/domain/errors';
import { ahoraIso, hoy, exigirFechaLocal, type FechaLocal } from '@/domain/dates';
import {
  exigirMontoDeCaja,
  resumenDeCaja,
  saldoDeCaja,
  dondeEstaElDinero,
  TIPOS_MOVIMIENTO_CAJA,
  type TipoMovimientoCaja,
  type ResumenDeCaja,
  type SaldoDeCaja,
  type DondeEstaElDinero,
  type MovimientoConCategoria,
} from '@/domain/cash';
import type { Centavos } from '@/domain/money';
import type { CashMovement } from '../types';

const MAX_CONCEPTO = 120;
const MAX_CATEGORIA = 60;
const MAX_NOTAS = 500;

// ---------------------------------------------------------------------------
// Registrar, editar y borrar
// ---------------------------------------------------------------------------

export interface DatosMovimiento {
  tipo: TipoMovimientoCaja;
  /** En pesos ya convertidos a centavos. Siempre positivo. */
  montoCentavos: Centavos;
  concepto: string;
  /** Si se omite, se usa hoy. */
  fecha?: FechaLocal;
  /** Solo tiene sentido en los gastos. */
  categoria?: string | null;
  notas?: string | null;
}

export async function registrarMovimiento(datos: DatosMovimiento): Promise<CashMovement> {
  const tipo = exigirTipo(datos.tipo);

  const movimiento: CashMovement = {
    id: nuevoId('caja'),
    fecha: exigirFechaLocal(datos.fecha ?? hoy(), 'fecha'),
    tipo,
    montoCentavos: exigirMontoDeCaja(datos.montoCentavos),
    concepto: exigirTexto(datos.concepto, 'concepto', { maximo: MAX_CONCEPTO }),
    // La categoria solo se guarda en los gastos: en un retiro no significa nada.
    categoria: tipo === 'gasto' ? textoOpcional(datos.categoria, 'categoria', MAX_CATEGORIA) : null,
    notas: textoOpcional(datos.notas, 'notas', MAX_NOTAS),
    creadoEn: ahoraIso(),
  };

  await db.cashMovements.add(movimiento);
  return movimiento;
}

export interface CambiosMovimiento {
  tipo?: TipoMovimientoCaja;
  montoCentavos?: Centavos;
  concepto?: string;
  fecha?: FechaLocal;
  categoria?: string | null;
  notas?: string | null;
}

export async function editarMovimiento(
  id: string,
  cambios: CambiosMovimiento,
): Promise<CashMovement> {
  return enEscritura([db.cashMovements], async () => {
    const actual = await db.cashMovements.get(id);
    if (actual === undefined) {
      throw new ErrorValidacion('No se encontro el movimiento de caja.');
    }

    const parche: Partial<CashMovement> = {};

    if (cambios.tipo !== undefined) parche.tipo = exigirTipo(cambios.tipo);
    if (cambios.montoCentavos !== undefined) {
      parche.montoCentavos = exigirMontoDeCaja(cambios.montoCentavos);
    }
    if (cambios.concepto !== undefined) {
      parche.concepto = exigirTexto(cambios.concepto, 'concepto', { maximo: MAX_CONCEPTO });
    }
    if (cambios.fecha !== undefined) parche.fecha = exigirFechaLocal(cambios.fecha, 'fecha');
    if (cambios.notas !== undefined) {
      parche.notas = textoOpcional(cambios.notas, 'notas', MAX_NOTAS);
    }

    /**
     * La categoria se recalcula siempre que cambie el tipo o la categoria misma.
     * Si un gasto pasa a retiro, su categoria deja de tener sentido y se limpia,
     * para que no quede un "Gas" pegado a un retiro personal.
     */
    const tipoFinal = parche.tipo ?? actual.tipo;
    if (cambios.categoria !== undefined || cambios.tipo !== undefined) {
      parche.categoria =
        tipoFinal === 'gasto'
          ? textoOpcional(cambios.categoria ?? actual.categoria, 'categoria', MAX_CATEGORIA)
          : null;
    }

    await db.cashMovements.update(id, parche);
    return { ...actual, ...parche };
  });
}

export async function eliminarMovimiento(id: string): Promise<void> {
  const actual = await db.cashMovements.get(id);
  if (actual === undefined) {
    throw new ErrorValidacion('No se encontro el movimiento de caja.');
  }
  await db.cashMovements.delete(id);
}

function exigirTipo(valor: unknown): TipoMovimientoCaja {
  if (!TIPOS_MOVIMIENTO_CAJA.includes(valor as TipoMovimientoCaja)) {
    throw new ErrorValidacion('El tipo de movimiento no es valido.', 'tipo');
  }
  return valor as TipoMovimientoCaja;
}

// ---------------------------------------------------------------------------
// Lecturas basicas
// ---------------------------------------------------------------------------

export async function movimientosEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<CashMovement[]> {
  return ordenar(await db.cashMovements.where('fecha').between(desde, hasta, true, true).toArray());
}

export async function movimientosHasta(hasta: FechaLocal): Promise<CashMovement[]> {
  return ordenar(await db.cashMovements.where('fecha').belowOrEqual(hasta).toArray());
}

export async function obtenerMovimiento(id: string): Promise<CashMovement | undefined> {
  return db.cashMovements.get(id);
}

/** Del mas reciente al mas viejo: en una lista de dinero interesa lo ultimo. */
function ordenar(movimientos: CashMovement[]): CashMovement[] {
  return movimientos.sort(
    (a, b) => b.fecha.localeCompare(a.fecha) || b.creadoEn.localeCompare(a.creadoEn),
  );
}

/** Categorias ya usadas, para sugerirlas sin obligar a elegir de una lista cerrada. */
export async function categoriasUsadas(): Promise<string[]> {
  const usadas = new Set<string>();
  for (const m of await db.cashMovements.toArray()) {
    if (m.tipo === 'gasto' && m.categoria !== null && m.categoria !== '') usadas.add(m.categoria);
  }
  return [...usadas].sort((a, b) => a.localeCompare(b, 'es'));
}

// ---------------------------------------------------------------------------
// Lo que se deriva de otras tablas
// ---------------------------------------------------------------------------

/**
 * Cobrado a clientes en un rango, por FECHA DE COBRO y no de entrega.
 *
 * Un pedido pagado sin fecha de cobro NO se cuenta en ningun rango, a proposito.
 * Son los pedidos de antes de que existiera el campo: no se sabe cuando entro ese
 * dinero, y meterlos en la semana de la entrega seria inventar el dato. Para el
 * saldo acumulado si cuentan, que es donde importa que el total cuadre.
 */
export async function cobradoEntre(desde: FechaLocal, hasta: FechaLocal): Promise<Centavos> {
  const pedidos = await db.orders.toArray();
  return pedidos
    .filter(
      (o) =>
        o.estado === 'activo' &&
        o.estadoPago === 'pagado' &&
        o.fechaPago != null &&
        o.fechaPago >= desde &&
        o.fechaPago <= hasta,
    )
    .reduce((s, o) => s + o.totalCentavos, 0);
}

/**
 * TODO lo cobrado hasta una fecha, contando tambien lo que no trae fecha de cobro.
 *
 * Aqui si entran los pedidos pagados sin fecha: el saldo tiene que cuadrar con el
 * dinero que de verdad paso por sus manos, y ese dinero entro aunque no se sepa que
 * dia. Excluirlos haria ver un faltante de caja que no existe.
 */
export async function cobradoHasta(hasta: FechaLocal): Promise<Centavos> {
  const pedidos = await db.orders.toArray();
  return pedidos
    .filter(
      (o) =>
        o.estado === 'activo' &&
        o.estadoPago === 'pagado' &&
        (o.fechaPago == null || o.fechaPago <= hasta),
    )
    .reduce((s, o) => s + o.totalCentavos, 0);
}

export async function comprasEntre(desde: FechaLocal, hasta: FechaLocal): Promise<Centavos> {
  const compras = await db.purchases.where('fecha').between(desde, hasta, true, true).toArray();
  return compras.filter((c) => c.anuladaPor === null).reduce((s, c) => s + c.totalCentavos, 0);
}

export async function comprasHasta(hasta: FechaLocal): Promise<Centavos> {
  const compras = await db.purchases.where('fecha').belowOrEqual(hasta).toArray();
  return compras.filter((c) => c.anuladaPor === null).reduce((s, c) => s + c.totalCentavos, 0);
}

/** Lo que le deben hoy. Es el mismo criterio de las cuentas por cobrar. */
export async function porCobrarCentavos(): Promise<Centavos> {
  const pedidos = await db.orders.toArray();
  return pedidos
    .filter((o) => o.estado === 'activo' && o.estadoPago === 'pendiente')
    .reduce((s, o) => s + o.totalCentavos, 0);
}

export interface PorCobrarPartido {
  /** Comida entregada y sin pagar. Se lo deben de verdad. */
  entregadoSinPagarCentavos: Centavos;
  /** Pedidos apalabrados que aun no entrega. */
  apalabradoCentavos: Centavos;
  /** Los dos juntos: el mismo numero que muestra la pantalla de Cobrar. */
  totalCentavos: Centavos;
}

/**
 * El por cobrar partido en lo exigible y lo que solo esta apalabrado.
 *
 * De donde salio la necesidad: ella pregunto si los $2,400 por cobrar incluian los
 * pedidos de la semana siguiente. Si los incluian. Al medirlo, solo $1,020 eran de
 * comida ya entregada y $1,380 eran pedidos por entregar, varios sin cocinar.
 *
 * Los dos numeros suman el total de siempre, asi que la pantalla de Cobrar y la de
 * Caja se pueden cuadrar entre ellas. Eso importa mas que ahorrarse un campo: dos
 * cifras del mismo dinero que no se reconcilian es como se pierde la confianza en
 * los numeros.
 */
export async function porCobrarPartido(): Promise<PorCobrarPartido> {
  const pedidos = await db.orders.toArray();
  const pendientes = pedidos.filter((o) => o.estado === 'activo' && o.estadoPago === 'pendiente');

  /**
   * El corte es la ENTREGA, no la fecha. Un pedido con fecha ya pasada que sigue
   * sin marcarse como entregado tampoco es exigible: mientras no salga de su
   * cocina, no hay nada que cobrar.
   */
  const entregadoSinPagarCentavos = pendientes
    .filter((o) => o.estadoEntrega === 'entregado')
    .reduce((s, o) => s + o.totalCentavos, 0);

  const apalabradoCentavos = pendientes
    .filter((o) => o.estadoEntrega !== 'entregado')
    .reduce((s, o) => s + o.totalCentavos, 0);

  return {
    entregadoSinPagarCentavos,
    apalabradoCentavos,
    totalCentavos: entregadoSinPagarCentavos + apalabradoCentavos,
  };
}

/** Valor de la despensa. Sale de la cache del inventario, igual que su pantalla. */
export async function enInventarioCentavos(): Promise<Centavos> {
  const insumos = await db.ingredients.toArray();
  return insumos.reduce((s, i) => s + i.valorCentavos, 0);
}

// ---------------------------------------------------------------------------
// Las dos consultas de la pantalla
// ---------------------------------------------------------------------------

export interface VistaDeCaja {
  desde: FechaLocal;
  hasta: FechaLocal;
  /** Lo que se movio en el periodo. */
  resumen: ResumenDeCaja;
  /** Saldo acumulado al final del periodo. */
  saldo: SaldoDeCaja;
  /** Las tres partes, nunca sumadas sin etiqueta. */
  donde: DondeEstaElDinero;
  /** Los movimientos capturados del periodo, del mas reciente al mas viejo. */
  movimientos: CashMovement[];
  /**
   * Cuantos pedidos pagados no traen fecha de cobro.
   *
   * Se reporta para poder decirlo en pantalla en lugar de que los numeros del
   * periodo salgan cortos sin explicacion.
   */
  cobrosSinFecha: number;
}

/**
 * Todo lo que la pantalla de Caja necesita, en una sola lectura.
 *
 * `gananciaDeLaComidaCentavos` entra como dato desde fuera y NO se recalcula aqui:
 * es el numero que produce el motor de ganancias, y tener dos formas de calcularlo
 * es como se acaba con dos cifras distintas.
 */
export async function verCaja(
  desde: FechaLocal,
  hasta: FechaLocal,
  gananciaDeLaComidaCentavos: Centavos | null,
): Promise<VistaDeCaja> {
  const [movimientos, cobradoPeriodo, comprasPeriodo, historicos, cobradoTotal, comprasTotal] =
    await Promise.all([
      movimientosEntre(desde, hasta),
      cobradoEntre(desde, hasta),
      comprasEntre(desde, hasta),
      movimientosHasta(hasta),
      cobradoHasta(hasta),
      comprasHasta(hasta),
    ]);

  const [porCobrar, inventario, pedidos] = await Promise.all([
    porCobrarPartido(),
    enInventarioCentavos(),
    db.orders.toArray(),
  ]);

  const resumen = resumenDeCaja({
    cobradoCentavos: cobradoPeriodo,
    comprasCentavos: comprasPeriodo,
    movimientos: movimientos as readonly MovimientoConCategoria[],
    gananciaDeLaComidaCentavos,
  });

  const saldo = saldoDeCaja({
    cobradoCentavos: cobradoTotal,
    comprasCentavos: comprasTotal,
    movimientos: historicos,
  });

  return {
    desde,
    hasta,
    resumen,
    saldo,
    donde: dondeEstaElDinero({
      saldo,
      entregadoSinPagarCentavos: porCobrar.entregadoSinPagarCentavos,
      apalabradoCentavos: porCobrar.apalabradoCentavos,
      enInventarioCentavos: inventario,
    }),
    movimientos,
    cobrosSinFecha: pedidos.filter(
      (o) => o.estado === 'activo' && o.estadoPago === 'pagado' && o.fechaPago == null,
    ).length,
  };
}

/** Saldo de hoy, para el tablero. Mas barato que armar la vista completa. */
export async function saldoActual(): Promise<SaldoDeCaja> {
  const referencia = hoy();
  const [cobrado, compras, movimientos] = await Promise.all([
    cobradoHasta(referencia),
    comprasHasta(referencia),
    movimientosHasta(referencia),
  ]);
  return saldoDeCaja({ cobradoCentavos: cobrado, comprasCentavos: compras, movimientos });
}
